import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * PageHero – left side: eyebrow + headline + description + optional CTA.
 *           right side: whatever the page passes as `rightContent`.
 */
const PageHero = ({
  eyebrow,
  title,
  description,
  cta,
  rightContent,
  tightBottom = false,
}) => {
  return (
    <section
      className={`hero-panel-bg ${tightBottom ? "pb-6" : "pb-12"} pt-6 sm:pt-10`}
      data-testid="page-hero"
    >
      <div className="max-w-[1280px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-6 relative z-10">
          {eyebrow && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 bg-blue-50 text-[#2563EB] font-semibold px-4 py-2 rounded-full text-sm mb-5"
              data-testid="hero-eyebrow"
            >
              {eyebrow}
            </motion.div>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="brand-headline text-4xl sm:text-5xl lg:text-[3.4rem] mb-6"
            data-testid="hero-title"
          >
            {title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
            className="text-slate-600 text-[15px] sm:text-base leading-relaxed max-w-[520px] mb-8"
            data-testid="hero-description"
          >
            {description}
          </motion.p>

          {cta && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link
                to={cta.to}
                data-testid="hero-cta-button"
                className="group inline-flex items-center gap-3 px-6 py-3 rounded-xl border-2 border-[#2563EB] text-[#2563EB] font-semibold hover:bg-[#2563EB] hover:text-white transition-colors duration-300"
              >
                {cta.label}
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            </motion.div>
          )}
        </div>

        <div className="lg:col-span-6 relative">{rightContent}</div>
      </div>
    </section>
  );
};

export default PageHero;
